/**
 * assetpicker.ts — the asset-selection model: filter + rank for the ticker
 * combobox, and the one owner of "which market is charted". No DOM API.
 *
 * WHY. Andrei, 2026-07-27, with Hyperliquid's own market picker as the reference:
 * *"when selecting a ticker the dropdown list is huge and it's hard to select
 * something. can we replace it with a dropdown list that allows us to enter several
 * letters to quicker find required ticker?"*
 *
 * Measured, not estimated: `/api/assets` returns **335** options on the default
 * hyperliquid feed, **103** of them `xyz:` HIP-3 names, versus 39 on bars_cache. A
 * native `<select>` only jumps by first letter, so reaching `xyz:SNDK` meant typing
 * `x` and scrolling past 102 siblings.
 *
 * WHY A MODULE AND NOT AN `if` IN main.ts. Gate 5 (vitest) exists because a state
 * bug inside `main.ts` was structurally unreachable by pytest and shipped green
 * through the other four gates. A ranking function in `main.ts` cannot be tested;
 * the same function here can.
 *
 * NO SCORING KNOB AND NO DEPENDENCY. 335 items need no fuzzy library and no
 * threshold: the order is deterministic — exact, then prefix, then substring, each
 * alphabetical. Andrei deletes knobs rather than tuning them (`fractal_n`), so there
 * is deliberately nothing here to calibrate.
 */

/** One row of `/api/assets`. */
export type AssetEntry = { asset: string; timeframes: string[] };

/** A match, with the ranking tier that put it there and where the query hit. */
export type AssetMatch = {
  asset: string;
  timeframes: string[];
  /** 0 exact · 1 prefix · 2 bare-tail prefix · 3 substring. Lower sorts first. */
  tier: 0 | 1 | 2 | 3;
  /** `[start, end)` of the matched span in `asset`, for highlighting. Empty query -> null. */
  hit: [number, number] | null;
  /** True for `xyz:` HIP-3 names — badged in the UI, because they behave differently. */
  hip3: boolean;
};

/** The bare instrument, i.e. `xyz:SNDK` -> `SNDK`. */
export const bareTail = (asset: string): string =>
  asset.includes(':') ? asset.slice(asset.indexOf(':') + 1) : asset;

export const isHip3 = (asset: string): boolean => asset.startsWith('xyz:');

/**
 * Filter + rank the universe for `query`.
 *
 * Behaviour that is load-bearing rather than incidental:
 *
 *  * **Empty query returns everything, in the SAME order the `<select>` had.** The
 *    picker must not reorder the list just because it gained a search box.
 *  * **The bare tail matches too.** `sndk` finds `xyz:SNDK`. The app already has to
 *    bridge that prefix mismatch (`tpActiveCoin` matches on the bare tail so the
 *    panel's row lights up for a chart asset), so the picker must agree with it —
 *    otherwise typing an instrument's actual name finds nothing.
 *  * **Exact beats prefix beats substring**, so `BTC` cannot be buried under
 *    `BTC.D` / `BTCBTC`-style neighbours. Ties break alphabetically, which is
 *    stable and needs no scoring.
 *  * **Case-insensitive both ways** (`XYZ:sndk`, `sndk`, `SnDk`).
 *
 * The returned `hit` span is on the FULL asset string even when the match was found
 * via the bare tail, so a caller can highlight without re-deriving the offset.
 */
export function filterAssets(assets: readonly AssetEntry[], query: string): AssetMatch[] {
  const q = query.trim().toLowerCase();
  const decorate = (a: AssetEntry, tier: AssetMatch['tier'], hit: [number, number] | null): AssetMatch =>
    ({ asset: a.asset, timeframes: a.timeframes, tier, hit, hip3: isHip3(a.asset) });

  if (!q) return assets.map((a) => decorate(a, 3, null));

  const out: AssetMatch[] = [];
  for (const a of assets) {
    const full = a.asset.toLowerCase();
    const tail = bareTail(a.asset).toLowerCase();
    const tailOffset = a.asset.length - tail.length;

    if (full === q || tail === q) {
      out.push(decorate(a, 0, full === q ? [0, q.length] : [tailOffset, tailOffset + q.length]));
    } else if (full.startsWith(q)) {
      out.push(decorate(a, 1, [0, q.length]));
    } else if (tail.startsWith(q)) {
      // `snd` -> `xyz:SNDK`: the instrument's own name, through a prefix the user
      // did not type. This tier is why the feature works at all for the 103 HIP-3
      // names, which are otherwise unreachable without typing `xyz:`.
      out.push(decorate(a, 2, [tailOffset, tailOffset + q.length]));
    } else {
      const at = full.indexOf(q);
      if (at >= 0) out.push(decorate(a, 3, [at, at + q.length]));
    }
  }

  return out.sort((x, y) =>
    x.tier !== y.tier ? x.tier - y.tier : x.asset.localeCompare(y.asset));
}

/**
 * Move the highlighted index by `delta`, WRAPPING.
 *
 * Wrapping is deliberate: with a filtered list of one or two items, an index that
 * sticks at the end makes ArrowDown feel broken. `-1` (nothing highlighted, e.g. a
 * fresh query) steps to the first item on the way down and the last on the way up.
 */
export function moveHighlight(current: number, delta: number, count: number): number {
  if (count <= 0) return -1;
  if (current < 0) return delta > 0 ? 0 : count - 1;
  return ((current + delta) % count + count) % count;
}

/**
 * What the dropdown should SAY, including when it has nothing.
 *
 * An empty list that looks identical to a failed fetch is the failure class this app
 * exists to avoid (the panel once rendered a failed dex read as a calm "No open
 * positions"), so "no match" is a message, never a blank box.
 */
export function pickerStatus(matches: readonly AssetMatch[], query: string, total: number): string {
  if (!total) return 'no assets — the feed returned an empty universe';
  if (!query.trim()) return `${total} markets`;
  if (!matches.length) return `no market matches “${query.trim()}”`;
  return `${matches.length} of ${total}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// WHICH MARKET IS CHARTED — one owner of the write AND its mirror
// ═══════════════════════════════════════════════════════════════════════════
// Andrei, 2026-07-28: *"when switching across different assets, we should switch
// asset selection dropdown too. if we're missing MVC component/controller in the
// web/app, we need to add it and use it."*
//
// THE DEFECT THIS EXISTS TO MAKE IMPOSSIBLE. The hidden `<select>` is the state
// holder and the visible input is a mirror of it, kept honest by a listener on the
// select's `change`. A programmatic `select.value = x` does NOT fire `change` (DOM
// spec), so every writer had to remember the mirror by hand — and measured on
// 2026-07-28, 1 of 3 did not: the panel's `showOnChart`, i.e. the one path every
// panel row click and the ticket's Chart button go through. Driving it live left
// `select = 'xyz:AMZN'` with the visible box still reading `BTC`, while the status
// bar and candles had moved. A confidently-wrong picture, which is the exact class
// of defect this app exists to remove.
//
// WHY THE MIRROR IS WRITTEN HERE AND NOT BY THE CALLER. Three call sites remembering
// a second step is a bug waiting for the fourth. `commitChartedAsset` performs the
// write and the mirror together, so they cannot be separated; `assetpicker.test.ts`
// additionally asserts that `main.ts` contains no bare `el.asset.value =` write
// outside the single adapter, so a future writer cannot re-open the hole.
//
// WHY NOT A NEW STATE CONTAINER. Rejected deliberately: the `<select>` is read in a
// dozen places and is already the single source of truth. A store next to it would
// be a SECOND source of truth about which market is charted — the drift these
// comments were written to prevent. This module owns the *rule*, not the state.

/** What happened to the request to chart a market. `unavailable` is a real outcome
 *  the caller must report rather than swallow: the two feeds carry different
 *  universes, so bars_cache genuinely cannot chart a Hyperliquid-only perp. */
export type ChartedAssetResult = 'switched' | 'already' | 'unavailable';

/**
 * The market selector, as the minimum this module needs.
 *
 * Structural rather than `HTMLSelectElement`/`HTMLInputElement` on purpose: the
 * decision is then reachable from vitest in a plain node environment, which is the
 * whole reason gate 5 exists (a `side` leak living in `main.ts` shipped green
 * through the other four gates because nothing could reach it).
 */
export type AssetSelection = {
  /** The charted market — the authoritative value. */
  value: string;
  /** Every market the CURRENT feed can chart. */
  readonly options: readonly string[];
  /** The visible combobox text. Must always end up equal to `value`. */
  mirror: string;
};

/** Would `asset` change the charted market, and can this feed chart it at all? */
export function decideChartedAsset(
  current: string,
  asset: string,
  options: readonly string[],
): ChartedAssetResult {
  if (!asset) return 'unavailable';
  if (current === asset) return 'already';
  return options.includes(asset) ? 'switched' : 'unavailable';
}

/**
 * Point the selector at `asset` and mirror it into the visible box, as ONE step.
 *
 * The mirror is written for `already` as well as `switched` — not just the branch
 * that moved. It is idempotent, and it is what makes the postcondition
 * unconditional: **after any non-`unavailable` return, `mirror === value`.** A
 * mirror written only on the moving branch left a stale box on boot, where the
 * `<select>` already defaults to the first option and nothing had "changed".
 *
 * `unavailable` touches nothing: a market this feed cannot chart must leave the
 * chart, the select and the box exactly as they were, so the caller's refusal note
 * describes a state the user can still see.
 */
export function commitChartedAsset(sel: AssetSelection, asset: string): ChartedAssetResult {
  const verdict = decideChartedAsset(sel.value, asset, sel.options);
  if (verdict === 'unavailable') return verdict;
  sel.value = asset;
  sel.mirror = sel.value;
  return verdict;
}

// ─────────────────────────────────────────────────────── which FEEDS are offered
// `decideChartedAsset` above refuses ONE market a feed cannot chart. This is the same
// rule one level up: a feed that can chart NOTHING must not be in the dropdown at all.

/** A feed, as the minimum this decision needs. Structural for the same reason
 *  `AssetSelection` is: reachable from vitest without a DOM. */
export type FeedOption = { id: string; assets?: number };

/**
 * The feeds worth offering, given what the backend says each one holds.
 *
 * WHY. Dev's `/api/health` reports `bars_cache: {path: "/opt/python/bars_cache",
 * exists: false, assets: 0}` and `/api/sources` agrees (`assets: 0`) -- the layer does
 * not ship the cache -- yet the dropdown still offered "bars_cache (what production
 * computed)". Selecting it loaded nothing and said nothing useful. Same dishonesty class
 * as a Setups tab that renders an empty state without having asked: AN OPTION THAT
 * CANNOT WORK MUST NOT BE OFFERED.
 *
 * `assets === 0` is the test, not `reachable`: dev reports bars_cache as REACHABLE (the
 * route answers) with zero series behind it, so reachability would have kept offering it.
 * `undefined` is NOT zero -- a backend that does not report a count is not thereby
 * declared empty, and every such feed is still offered.
 *
 * NEVER EMPTIES THE LIST. If no feed reports any assets the whole list is returned
 * unchanged: a dropdown with nothing in it cannot be charted from and hides the
 * backend's problem instead of showing it, which is the failure this function exists to
 * prevent, inverted.
 */
export function offerableFeeds<T extends FeedOption>(feeds: readonly T[]): T[] {
  const usable = feeds.filter((f) => f.assets !== 0);
  return usable.length ? usable : [...feeds];
}

